import {Injectable} from '@angular/core';
import {Observable, catchError, map, of, shareReplay} from 'rxjs';
import {API_URL} from '../constants';
import {UserApiService} from './user-api.service';

/**
 * Member profile photos as ready-to-show object URLs.
 *
 * A bare `<img src=".../profile/viewimage/...">` shows a broken image on UAT/prod: API Gateway only returns an image
 * as binary when the request's first Accept type is one of its BinaryMediaTypes (image/jpeg, image/png), and a
 * browser's `<img>` asks for image/avif first, so it gets base64 text back. The rest of the app avoids this by
 * fetching the photo as a blob with `Accept: image/jpeg` (UserApiService.getProfileImageAsBlob); this does the same
 * once per photo and shares the result, so a feed of ten cards by one author makes one request.
 */
@Injectable({
    providedIn: 'root'
})
export class ProfileImageService {
    private cache = new Map<string, Observable<string | null>>();

    constructor(private userApi: UserApiService) {
    }

    /** An object URL for the photo, or null when the member has none or it cannot be loaded (show initials instead). */
    photo(userId: string, file?: string | null): Observable<string | null> {
        if (!userId || !file || file === 'null') return of(null);
        const key = `${userId}/${file}`;
        let photo$ = this.cache.get(key);
        if (!photo$) {
            photo$ = this.userApi.getProfileImageAsBlob(`${API_URL}profile/viewimage/${userId}/${file}`).pipe(
                map(blob => blob && blob.size > 0 ? URL.createObjectURL(blob) : null),
                catchError(() => of(null)),
                shareReplay(1)
            );
            this.cache.set(key, photo$);
        }
        return photo$;
    }
}
